import { formatCurrency } from '@adornis/accounting/base/api/currencyFormat.js';
import { OposAPI } from '@adornis/accounting/opos/api/OposAPI.js';
import { openOpenItemExplanationDialog } from '@adornis/accounting/opos/client/OpenItemExplanation.js';
import { type OpenItem, OpenItemSettlement, OpenItemStatus } from '@adornis/accounting/opos/entities/OpenItem.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { usePopover } from '@adornis/functional-lit/use-popover.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { BillingRhythm } from '../../../../billingPositions/entities/BillingPosition.js';
import type { GroupParticipantRow } from '../../api/GroupsDetailsAPI.js';
import { MemberInlineAccountingEntryMonthsTooltip } from './MemberInlineAccountingEntryMonthsTooltip.js';

/**
 * Our exports write a Belegfeld of the group's Kürzel and the month they book in: `Kürzel-yyMM-Debitor` in the
 * Buchungsstapel (`getCSVLineDTVFBuchungsstapel`), `Kürzel` + `yyMM` + mandate id in the Zahllauf
 * (`getMandatsNummer`) - either can become the Vorgang's identifier. The Kürzel matters: a person's other billing
 * positions can reference the booking too. DATEV keeps only some characters in a Belegfeld and swaps the rest
 * (KBS_HBFS_KF comes back as KBS%HBFS%KF), so anything but a letter or digit matches any character.
 *
 * Older forms of the same Vorgänge: Spende was booked as `S-yyMM-n` until 12/2025 (the export's fallback Kürzel, 30k
 * Soll rows against 8009), the Zahllauf of 12/2025 wrote `KF-yyMM-<mandate>`, and the Buchhaltung collects
 * Instrumentenmiete itself as `IM-yyMM-B-n`.
 * @returns the month a Vorgang of the group's Kürzel was booked in (UTC month start), `null` for any other Vorgang
 */
const belegfeldMonthOf = (kuerzel: string) => {
  const prefix = [...kuerzel].map(char => (/[A-Za-z0-9]/.test(char) ? char : '.')).join('');
  const prefixes = kuerzel === 'Spende' ? `(?:${prefix}|S)` : prefix;
  const belegfeld = new RegExp(`^${prefixes}(?:-(\\d{4})-(?:B-)?\\d+|-?(\\d{4})-?[0-9a-f]{24})$`);
  return (item: Pick<OpenItem, 'processIdentifiers'>): number | null => {
    const months = item.processIdentifiers.flatMap(id => {
      const match = belegfeld.exec(id);
      const yyMM = match?.[1] ?? match?.[2];
      return yyMM ? [Date.UTC(2000 + Number(yyMM.slice(0, 2)), Number(yyMM.slice(2)) - 1, 1)] : [];
    });
    return months.length ? Math.max(...months) : null;
  };
};

/** what only we know about the row's state: our export, and which Vorgang the badge follows */
const contextOf = (props: { exportPendingSince: Maybe<string>; vorgangMonth: Maybe<string>; vorgangCount: number }) => {
  if (props.exportPendingSince)
    return `Von uns am ${props.exportPendingSince} exportiert. Die Buchhaltung hat den Buchungsstapel noch nicht gebucht – sobald er zurückkommt, folgt der Status seinem Vorgang.`;
  if (!props.vorgangMonth)
    return 'Die Buchhaltung hat noch keinen Vorgang dieser Abrechnungsposition gebucht, und die Buchung wurde noch nicht mit „Exportieren und sperren“ abgerechnet.';
  return `Der Status folgt dem neuesten Vorgang dieser Abrechnungsposition (${props.vorgangMonth}) und nur seinen eigenen Buchungen – Geld anderer Vorgänge desselben Kontos zählt nicht. Ältere Vorgänge stehen in der Historie.${props.vorgangCount > 1 ? ` Der Monat liegt in ${props.vorgangCount} Vorgängen; erklärt wird der erste.` : ''}`;
};

/**
 * The same bordered badge as the SEPA and Abrechnungsposition columns. Tailwind only sees literal class tokens, so
 * every tone spells its classes out instead of interpolating the colour name.
 */
const STATE_TONES = {
  success: {
    container: 'border-success/35',
    icon: 'text-success',
    button: 'border-success/35 text-success/80 hover:bg-success/10 hover:border-success/60',
  },
  warning: {
    container: 'border-warning/45',
    icon: 'text-warning',
    button: 'border-warning/45 text-warning/90 hover:bg-warning/10 hover:border-warning/70',
  },
  error: {
    container: 'border-error/35',
    icon: 'text-error',
    button: 'border-error/35 text-error/80 hover:bg-error/10 hover:border-error/60',
  },
  info: {
    container: 'border-info/40',
    icon: 'text-info',
    button: 'border-info/40 text-info/80 hover:bg-info/10 hover:border-info/60',
  },
  neutral: {
    container: 'border-base-300/80',
    icon: 'text-base-content/70',
    button: 'border-base-300 text-base-content/70 hover:bg-base-200/70 hover:border-base-300',
  },
} as const;

/** what the badge says: the step, an amount still owed or held, and when */
type PaymentState = {
  tone: keyof typeof STATE_TONES;
  icon: string;
  label: string;
  /** the history's badge for the same state */
  badgeClass: string;
  amount?: string;
  detail?: string;
  title?: string;
};

/**
 * How Vorgänge stand by their own bookings: money of another Vorgang on the same account says nothing about this
 * one. A Vorgang an EB-Wert carried into the next year reads the last balance of its chain: a booking there closed
 * it (`balanceClosed`), or its own saldo is still open.
 *
 * Only the Jahresabschluss closes a Vorgang without its own bookings (`settledByAccount`): the fiscal year ended with
 * the account at zero and the Buchhaltung carried nothing forward, so only what was booked after the cut still
 * counts (`openMinorUnit`). A Klassenfahrt paid before its charge was booked read "Offen" here otherwise.
 */
const vorgangStateOf = (items: OpenItem[]): PaymentState => {
  const closedByBalance = (item: OpenItem) => item.status === OpenItemStatus.CARRIED_FORWARD && item.balanceClosed;
  const settlementOf = (item: OpenItem) => (closedByBalance(item) ? item.balanceSettlement : item.settlement);
  const ownOf = (item: OpenItem) =>
    closedByBalance(item) ? 0 : item.settledByAccount ? item.openMinorUnit : item.signedSumMinorUnit;
  const own = items.reduce((sum, item) => sum + ownOf(item), 0);
  const has = (settlement: OpenItemSettlement) => items.some(item => settlementOf(item) === settlement);
  if (own > 0)
    return has(OpenItemSettlement.RETURNED)
      ? {
          tone: 'error',
          icon: 'icon-[material-symbols--error-outline-rounded]',
          label: 'Rücklastschrift',
          badgeClass: 'badge-error',
          amount: formatCurrency(own),
        }
      : {
          tone: 'error',
          icon: 'icon-[material-symbols--error-outline-rounded]',
          label: 'Offen',
          badgeClass: 'badge-error badge-outline',
          amount: formatCurrency(own),
        };
  if (own < 0)
    return {
      tone: 'info',
      icon: 'icon-[material-symbols--account-balance-wallet-outline]',
      label: 'Guthaben',
      badgeClass: 'badge-info badge-outline',
      amount: formatCurrency(-own),
    };
  if (items.every(item => settlementOf(item) === OpenItemSettlement.CANCELLED))
    return {
      tone: 'neutral',
      icon: 'icon-[material-symbols--cancel-rounded]',
      label: 'Storniert',
      badgeClass: 'badge-neutral badge-outline',
    };
  // the bank has not credited the Lastschrift run yet: submitted, not paid
  if (has(OpenItemSettlement.AWAITING_BANK))
    return {
      tone: 'warning',
      icon: 'icon-[material-symbols--pending-rounded]',
      label: 'Eingezogen',
      badgeClass: 'badge-warning badge-outline',
    };
  if (has(OpenItemSettlement.PAID))
    return {
      tone: 'success',
      icon: 'icon-[material-symbols--check-circle-rounded]',
      label: 'Bezahlt',
      badgeClass: 'badge-success badge-outline',
    };
  // its own bookings net to zero without money: moved to another personal account
  return {
    tone: 'neutral',
    icon: 'icon-[material-symbols--balance]',
    label: 'Ausgeglichen',
    badgeClass: 'badge-ghost',
  };
};

/**
 * What the accounting processes of the row's bookings say. The open items referencing the bookings decide it, never
 * the raw entries: the journal is exported double-sided, so the Erlös and Bank rows of a Vorgang reference the
 * booking too, and a Haben among them is no payment.
 *
 * Only the newest Vorgang of the group's Kürzel decides, by the month its Belegfeld names - not the day it starts (a
 * transfer can come before the charge), and not older Vorgänge (they stand in the history). Not our markers either:
 * an export without "sperren" writes none and the Buchhaltung books it all the same, and a migrated marker names the
 * end of the month before. A marker of a later month than that Vorgang is an export the Buchhaltung has not booked
 * yet. A collection goes through three steps: our export ("Exportiert"), its Buchungsstapel in the Buchhaltung (the
 * Vorgang exists - "Offen" for a Selbstzahler, "Eingezogen" for a Lastschrift the bank has not credited yet), and the
 * money ("Bezahlt").
 */
export const MemberInlineAccountingEntryInformation = stateful(
  (props: { participantRow: GroupParticipantRow; disabled: boolean }) => {
    const bookingIDs = props.participantRow.bookings.map(booking => booking._id);
    const { value: items, loading } = useQuery(
      () =>
        OposAPI.getOpenItems(bookingIDs)({
          _id: 1,
          status: 1,
          settlement: 1,
          signedSumMinorUnit: 1,
          openMinorUnit: 1,
          settledByAccount: 1,
          balanceClosed: 1,
          balanceSettlement: 1,
          processIdentifiers: 1,
          firstTimestamp: 1,
        }),
      [bookingIDs.join()],
    );
    // the history opens on click and stays open for reading, so a popover instead of a hover tooltip
    const history = usePopover({ positionArea: 'bottom', closeOnClickOutside: true });

    // a group without an Abrechnungsposition bills nobody: "Nicht exportiert" would read as work left undone
    const billingPosition = props.participantRow.group?.billingPosition;
    if (!billingPosition) return nothing;

    if (loading) {
      return html`
        <div class=${['flex items-center py-1.5 mx-1', props.disabled ? 'opacity-70' : ''].join(' ')}>
          <span class="loading loading-spinner loading-sm text-base-content/70"></span>
        </div>
      `;
    }

    const all = items ?? [];
    // the export falls back to "S" without a Kürzel, so does the Belegfeld
    const belegfeldMonth = belegfeldMonthOf(billingPosition.kuerzel ?? 'S');
    // Buchungsdaten are canonical calendar days, so the month is read in UTC
    const monthOf = (day: Maybe<Date>) => (day ? Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), 1) : null);
    const latestMonth = Math.max(...all.map(item => belegfeldMonth(item) ?? -Infinity));
    const latestItems = all.filter(item => belegfeldMonth(item) === latestMonth);
    const exportedOn = props.participantRow.exportedOn;
    /**
     * A one-time position is charged once: when its Vorgang is booked, a later marker is no export still on its way
     * (Idar-Oberstein was booked in July from an export without "sperren", locked in August).
     */
    const chargedOnce = billingPosition.rhythm === BillingRhythm.ONCE_CUSTOM;
    const exportPending = !!exportedOn && (!latestItems.length || (!chargedOnce && monthOf(exportedOn)! > latestMonth));
    const exportedOnLabel = exportedOn ? formatCalendarDay(exportedOn, 'dd.MM.yyyy') : undefined;
    const latestMonthLabel = latestItems.length ? formatCalendarDay(new Date(latestMonth), 'MM/yyyy') : undefined;
    const processes = [
      ...all.flatMap(item => {
        const month = belegfeldMonth(item) ?? monthOf(item.firstTimestamp);
        return month ? [{ month: new Date(month), ...vorgangStateOf([item]) }] : [];
      }),
      ...(exportPending
        ? [{ month: new Date(monthOf(exportedOn)!), label: 'Exportiert', badgeClass: 'badge-info badge-outline' }]
        : []),
    ];

    const state: PaymentState = exportPending
      ? {
          tone: 'info',
          icon: 'icon-[material-symbols--send-rounded]',
          label: 'Exportiert',
          badgeClass: 'badge-info badge-outline',
          detail: exportedOnLabel,
          title: 'Von uns exportiert - der Buchungsstapel ist noch nicht in der Buchhaltung',
        }
      : latestItems.length
        ? { ...vorgangStateOf(latestItems), detail: latestMonthLabel }
        : {
            tone: 'neutral',
            icon: 'icon-[material-symbols--schedule-rounded]',
            label: 'Nicht exportiert',
            badgeClass: 'badge-ghost',
          };
    const tone = STATE_TONES[state.tone];

    const explain = () =>
      openOpenItemExplanationDialog({
        itemID: exportPending ? undefined : latestItems[0]?._id,
        title: `Zahlungsstatus: ${state.label}`,
        context: html`<p class="text-sm">
          ${contextOf({
            exportPendingSince: exportPending ? exportedOnLabel : undefined,
            vorgangMonth: latestMonthLabel,
            vorgangCount: latestItems.length,
          })}
        </p>`,
      });

    // the whole badge opens the explanation; the history button inside stops its own click
    return html`
      <div
        role="button"
        tabindex="0"
        aria-label=${`Zahlungsstatus ${state.label} erklärt`}
        class=${[
          'inline-flex cursor-pointer items-center gap-2 rounded-lg border bg-base-100 px-3 py-1.5 shadow-sm mx-1 hover:bg-base-200/60',
          tone.container,
          props.disabled ? 'opacity-70' : '',
        ].join(' ')}
        title=${state.title ?? nothing}
        @click=${(e: Event) => {
          e.stopPropagation();
          void explain();
        }}
        @keydown=${(e: KeyboardEvent) => {
          // the history button inside handles its own keys
          if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          void explain();
        }}
      >
        <span class=${`${state.icon} text-base ${tone.icon}`}></span>
        <span class="text-sm font-medium text-base-content">${state.label}</span>
        ${state.amount ? html`<span class="text-sm font-semibold text-base-content">${state.amount}</span>` : nothing}
        ${state.detail ? html`<span class="text-sm text-base-content/70">${state.detail}</span>` : nothing}
        ${
          processes.length
            ? html`
                <button
                  type="button"
                  class=${`btn btn-ghost btn-xs btn-circle border ${tone.button}`}
                  aria-label="Vorgänge"
                  style="anchor-name: ${history.anchorName};"
                  ${ref(history.anchorRef)}
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    if (history.visible) history.hide();
                    else history.show();
                  }}
                >
                  <span class="icon-[material-symbols--history-rounded] text-base"></span>
                </button>
                <div
                  popover="manual"
                  tabindex="-1"
                  id=${history.popoverId}
                  style="position-anchor: ${history.anchorName}; ${history.getAnchorStyles()}"
                  class="bg-base-100 text-base-content shadow-2xl border border-base-300 rounded-box p-2"
                  ${ref(history.popoverRef)}
                  @click=${(e: Event) => e.stopPropagation()}
                >
                  ${MemberInlineAccountingEntryMonthsTooltip({ processes })}
                </div>
              `
            : nothing
        }
      </div>
    `;
  },
);
